import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Divider
} from '@mui/material';
import { Psychology as AiIcon } from '@mui/icons-material';
import analyticsService from '../../services/analyticsService';
import { useAuth } from '../../context/AuthContext';
import NaturalLanguageQuery from '../nlquery/NaturalLanguageQuery';
import SpecimenSiteChart from './SpecimenSiteChart';
import DiseaseChart from './DiseaseChart';
import InstitutionChart from './InstitutionChart';
import StatusChart from './StatusChart';
import TimelineChart from './TimelineChart';
import StorageChart from './StorageChart';
import ProjectVolumesChart from './ProjectVolumesChart';

const Analytics = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nlQueryOpen, setNlQueryOpen] = useState(false);
  const [data, setData] = useState({
    overview: null,
    specimenSite: [],
    disease: [],
    institution: [],
    status: [],
    timeline: [],
    storage: [],
    projectVolumes: [],
    extractionStatus: [],
    availabilityStatus: []
  });

  // Check if user has access to NL query feature (admin and lab_manager only)
  const hasNlQueryAccess = currentUser?.role === 'admin' || currentUser?.role === 'lab_manager';

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        overview,
        specimenSite,
        disease,
        institution,
        status,
        timeline,
        storage,
        projectVolumes,
        extractionStatus,
        availabilityStatus
      ] = await Promise.all([
        analyticsService.getOverview(),
        analyticsService.getSpecimensBySite(),
        analyticsService.getSpecimensByDisease(),
        analyticsService.getSpecimensByInstitution(),
        analyticsService.getSpecimensByStatus(),
        analyticsService.getSpecimensTimeline(),
        analyticsService.getStorageDistribution(),
        analyticsService.getProjectVolumes(),
        analyticsService.getExtractionStatus(),
        analyticsService.getAvailabilityStatus()
      ]);

      setData({
        overview,
        specimenSite,
        disease,
        institution,
        status,
        timeline,
        storage,
        projectVolumes,
        extractionStatus,
        availabilityStatus
      });
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Analytics Dashboard
        </Typography>
        {hasNlQueryAccess && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AiIcon />}
            onClick={() => setNlQueryOpen(true)}
          >
            Ask AI a Question
          </Button>
        )}
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Explore your data with pre-built visualizations{hasNlQueryAccess && ' or ask custom questions using natural language'}.
      </Typography>

      <Divider sx={{ mb: 4 }} />

      {/* Charts Grid */}
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Pre-Built Analytics
      </Typography>
      <Grid container spacing={3}>
        {/* Specimen Type Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Specimens by Type
            </Typography>
            <SpecimenSiteChart data={data.specimenSite} />
          </Paper>
        </Grid>

        {/* Disease Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Top Diseases (Top 20)
            </Typography>
            <DiseaseChart data={data.disease} />
          </Paper>
        </Grid>

        {/* Institution Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Top Institutions (Top 20)
            </Typography>
            <InstitutionChart data={data.institution} />
          </Paper>
        </Grid>

        {/* Storage Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Storage Distribution by Freezer
            </Typography>
            <StorageChart data={data.storage} />
          </Paper>
        </Grid>

        {/* Collection Timeline */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Collection Timeline
            </Typography>
            <TimelineChart data={data.timeline} />
          </Paper>
        </Grid>

        {/* Project Volumes */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '500px' }}>
            <Typography variant="h6" gutterBottom>
              Top Projects by Specimen Count
            </Typography>
            <ProjectVolumesChart data={data.projectVolumes} />
          </Paper>
        </Grid>
      </Grid>

      {/* Natural Language Query Dialog */}
      <NaturalLanguageQuery
        open={nlQueryOpen}
        onClose={() => setNlQueryOpen(false)}
      />
    </Container>
  );
};

export default Analytics;
